import { useState, useCallback } from 'react';
import { apiService } from '../services/api.service';
import { User, CreateUserRequest, SetPasswordRequest } from '../types';

interface UseUsersState {
  users: User[];
  isLoading: boolean;
  error: string | null;
  paginationToken?: string;
  hasMore: boolean;
  togglingAdminUsername: string | null;
  togglingEnabledUsername: string | null;
}

interface UseUsersReturn extends UseUsersState {
  fetchUsers: (reset?: boolean) => Promise<void>;
  createUser: (request: CreateUserRequest) => Promise<User>;
  deleteUser: (username: string) => Promise<void>;
  setUserPassword: (username: string, request: SetPasswordRequest) => Promise<void>;
  toggleAdmin: (username: string) => Promise<{ isAdmin: boolean }>;
  toggleEnabled: (username: string) => Promise<{ enabled: boolean }>;
  clearError: () => void;
}

export function useUsers(): UseUsersReturn {
  const [state, setState] = useState<UseUsersState>({
    users: [],
    isLoading: false,
    error: null,
    paginationToken: undefined,
    hasMore: true,
    togglingAdminUsername: null,
    togglingEnabledUsername: null,
  });

  const fetchUsers = useCallback(
    async (reset = false): Promise<void> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const token = reset ? undefined : state.paginationToken;
        const response = await apiService.listUsers(20, token);

        setState((prev) => ({
          ...prev,
          users: reset ? response.users : [...prev.users, ...response.users],
          paginationToken: response.paginationToken,
          hasMore: !!response.paginationToken,
          isLoading: false,
        }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch users';
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: message,
        }));
      }
    },
    [state.paginationToken]
  );

  const createUser = useCallback(async (request: CreateUserRequest): Promise<User> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const user = await apiService.createUser(request);
      setState((prev) => ({
        ...prev,
        users: [user, ...prev.users],
        isLoading: false,
      }));
      return user;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create user';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw error;
    }
  }, []);

  const deleteUser = useCallback(async (username: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await apiService.deleteUser(username);
      setState((prev) => ({
        ...prev,
        users: prev.users.filter((u) => u.username !== username),
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete user';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw error;
    }
  }, []);

  const setUserPassword = useCallback(
    async (username: string, request: SetPasswordRequest): Promise<void> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        await apiService.setUserPassword(username, request);
        setState((prev) => ({ ...prev, isLoading: false }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to set password';
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: message,
        }));
        throw error;
      }
    },
    []
  );

  const toggleAdmin = useCallback(async (username: string): Promise<{ isAdmin: boolean }> => {
    setState((prev) => ({ ...prev, togglingAdminUsername: username, error: null }));

    try {
      const result = await apiService.toggleAdmin(username);
      setState((prev) => ({
        ...prev,
        users: prev.users.map((u) =>
          u.username === username ? { ...u, isAdmin: result.isAdmin } : u
        ),
        togglingAdminUsername: null,
      }));
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to toggle admin status';
      setState((prev) => ({
        ...prev,
        togglingAdminUsername: null,
        error: message,
      }));
      throw error;
    }
  }, []);

  const toggleEnabled = useCallback(async (username: string): Promise<{ enabled: boolean }> => {
    setState((prev) => ({ ...prev, togglingEnabledUsername: username, error: null }));

    try {
      const result = await apiService.toggleEnabled(username);
      setState((prev) => ({
        ...prev,
        users: prev.users.map((u) =>
          u.username === username ? { ...u, enabled: result.enabled } : u
        ),
        togglingEnabledUsername: null,
      }));
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to toggle enabled status';
      setState((prev) => ({
        ...prev,
        togglingEnabledUsername: null,
        error: message,
      }));
      throw error;
    }
  }, []);

  const clearError = useCallback((): void => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    fetchUsers,
    createUser,
    deleteUser,
    setUserPassword,
    toggleAdmin,
    toggleEnabled,
    clearError,
  };
}
